import React, { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Activity, CheckCircle2, AlertTriangle, RefreshCw, Database, Clock, Server, HardDrive,
} from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import { formatDateTime } from "@/lib/formatDate";

function fmtRelative(iso) {
  if (!iso) return "—";
  const now = new Date();
  const target = new Date(iso);
  const diffMs = target - now;
  const sec = Math.round(diffMs / 1000);
  if (Math.abs(sec) < 60) return `in ${sec}s`;
  const min = Math.round(sec / 60);
  if (Math.abs(min) < 60) return `in ${min}m`;
  const hr = Math.round(min / 60);
  if (Math.abs(hr) < 48) return `in ${hr}h`;
  return formatDateTime(target);
}

function fmtUptime(s) {
  if (!s || s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

export default function SystemHealthCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await apiClient.get("/admin/system-health");
      setData(r.data);
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || "Health-Check fehlgeschlagen");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(() => load(true), 15000);
    return () => clearInterval(id);
  }, [load]);

  const overallOk = data?.ok;
  const overallTone = overallOk ? "emerald" : "rose";

  return (
    <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="super-admin-health-card">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Activity className={`h-5 w-5 ${overallOk ? "text-emerald-600" : "text-rose-600"}`} />
          <h2 className="text-lg font-semibold text-slate-900">System-Health</h2>
          {data && (
            <Badge
              variant="outline"
              className={`text-xs font-medium ${
                overallOk
                  ? "border-emerald-300 text-emerald-700 bg-emerald-50"
                  : "border-rose-300 text-rose-700 bg-rose-50"
              }`}
              data-testid="health-overall-badge"
            >
              {overallOk ? "ALLES OK" : "FEHLER"}
            </Badge>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={() => load()} disabled={loading} data-testid="health-refresh-btn">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          Aktualisieren
        </Button>
      </div>

      {!data ? (
        <div className="py-8 text-center text-sm text-slate-400">Lade …</div>
      ) : (
        <>
          {/* Top-Stats: Mongo, Scheduler, Uptime, Errors */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <div
              className={`p-3 rounded-lg border ${data.storage?.ok ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`}
              data-testid="health-stat-storage"
            >
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
                <HardDrive className="h-3.5 w-3.5" /> Datei-Speicher (S3)
              </div>
              <div className="mt-1 text-lg font-semibold text-slate-900">
                {data.storage?.ok ? "✓ Verbunden" : data.storage?.configured ? "✗ Fehler" : "✗ Nicht konfiguriert"}
              </div>
              <div className="text-[10px] text-slate-500 truncate" title={data.storage?.error || data.storage?.endpoint}>
                {data.storage?.ok
                  ? `${data.storage.bucket} · ${data.storage.object_count} Dateien`
                  : data.storage?.error || "S3_* Variablen setzen"}
              </div>
            </div>

            <div
              className={`p-3 rounded-lg border ${data.mongo_ok ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`}
              data-testid="health-stat-mongo"
            >
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
                <Database className="h-3.5 w-3.5" /> MongoDB
              </div>
              <div className="mt-1 text-lg font-semibold text-slate-900">
                {data.mongo_ok ? "✓ Live" : "✗ Down"}
              </div>
              <div className="text-[10px] text-slate-500">Ping {data.mongo_ping_ms} ms</div>
            </div>

            <div
              className={`p-3 rounded-lg border ${data.scheduler_running ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`}
              data-testid="health-stat-scheduler"
            >
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
                <Clock className="h-3.5 w-3.5" /> Scheduler
              </div>
              <div className="mt-1 text-lg font-semibold text-slate-900">
                {data.scheduler_running ? "✓ Running" : "✗ Stopped"}
              </div>
              <div className="text-[10px] text-slate-500">{data.cron_jobs.length} Cron-Jobs</div>
            </div>

            <div className="p-3 rounded-lg border border-slate-200 bg-slate-50" data-testid="health-stat-uptime">
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
                <Server className="h-3.5 w-3.5" /> Backend-Uptime
              </div>
              <div className="mt-1 text-lg font-semibold text-slate-900">{fmtUptime(data.uptime_seconds)}</div>
              <div className="text-[10px] text-slate-500">seit {formatDateTime(data.backend_started_at)}</div>
            </div>

            <div
              className={`p-3 rounded-lg border ${
                Object.values(data.errors_last_24h || {}).some((v) => v > 0)
                  ? "border-amber-200 bg-amber-50"
                  : "border-emerald-200 bg-emerald-50"
              }`}
              data-testid="health-stat-errors"
            >
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
                <AlertTriangle className="h-3.5 w-3.5" /> Fehler 24h
              </div>
              <div className="mt-1 text-lg font-semibold text-slate-900">
                {Object.values(data.errors_last_24h || {}).reduce((a, b) => a + (b > 0 ? b : 0), 0)}
              </div>
              <div className="text-[10px] text-slate-500 truncate">
                {Object.entries(data.errors_last_24h || {})
                  .filter(([, v]) => v > 0)
                  .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`)
                  .join(" · ") || "keine Fehler"}
              </div>
            </div>
          </div>

          {/* Cron-Jobs Tabelle */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-2">Cron-Jobs ({data.cron_jobs.length})</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="health-cron-table">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-1.5 px-2">Job</th>
                    <th className="py-1.5 px-2">Trigger</th>
                    <th className="py-1.5 px-2">Nächster Lauf</th>
                    <th className="py-1.5 px-2 text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.cron_jobs.map((j) => (
                    <tr key={j.id} className="border-b border-slate-100" data-testid={`health-cron-row-${j.id}`}>
                      <td className="py-1.5 px-2 font-mono text-slate-900">{j.id}</td>
                      <td className="py-1.5 px-2 font-mono text-slate-500 text-[11px] truncate max-w-[200px]" title={j.trigger}>{j.trigger}</td>
                      <td className="py-1.5 px-2 text-slate-700">
                        {fmtRelative(j.next_run_time)}
                        <div className="text-[10px] text-slate-400">{j.next_run_time ? formatDateTime(j.next_run_time) : "—"}</div>
                      </td>
                      <td className="py-1.5 px-2 text-right">
                        {j.pending ? (
                          <Badge variant="outline" className="text-xs border-amber-300 text-amber-700 bg-amber-50">PAUSED</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs border-emerald-300 text-emerald-700 bg-emerald-50">
                            <CheckCircle2 className="h-3 w-3 mr-0.5" /> SCHEDULED
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-2 text-[10px] text-slate-400 text-right">
              Auto-Refresh alle 15s · Stand {new Date(data.timestamp).toLocaleTimeString("de-CH")}
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
